import { FC, useEffect, useState } from 'react';
import { Text, Box, ToggleSwitch, Spinner, SpinnerSize, Button, Icon } from '@bechtel/gds';
import { WeldDetailProps } from './types';
import { ErrorBoundary, useEnvProvider, ProjectAppTypes, useAppUser, WeldMilestoneType } from '@bechtel/client-common';
import { some, filter, find, get, join, map } from 'lodash';
import { Trans } from '@lingui/macro';
import { SentBackReasonContainerStyled, WeldMilestonesContainerStyled, WeldTagStyled } from './styled';
import { WeldMilestone } from '../weld-milestone';
import { WeldDetailTabs } from './weld-detail-tabs';
import { FitupMilestoneMock } from '../types';
import {
  CommodityMilestone,
  CommodityStatusCode,
  CommodityWrapper,
  MilestoneActivity,
  MilestoneStatusCode,
  PagingInput,
  PersonaType,
  RejectFitupReason,
  RejectWeldSubReason,
  Weld,
} from '@bechtel/types';
import { useLingui } from '@lingui/react';
import {
  SendBackReasonMessages,
  SendBackSubReasonMessages,
  useCancelFitupRequest,
  WeldActionErrorExt,
} from '@bechtel/client-weld-common';
import { useWorkPackage } from '~/detail/context';
import { useWorkPackagePermissions } from '~/hooks';
import { useLazyQuery, NetworkStatus } from '@apollo/client';
import { GET_WELDS_DETAIL_BY_IDS } from '@bechtel/client-gql';
// @ts-ignore: tsc declarations not recognising types/index.d.ts
import { ReactComponent as EmptyWeldDetailIcon } from '@bechtel/client-assets/dist/images/empty_state_fitup_weld.svg';
import { WeldFlangeConnectionAction } from './action';
import { isBefore, addDays, parseISO } from 'date-fns';
import { MilestoneError } from '~/detail/commodities';

export const WeldDetail: FC<WeldDetailProps> = ({
  weldId,
  isFitupRequired,
  showItrCertDialog,
  setIsFitupRequired,
  setMilestoneData,
  close,
}) => {
  const [cancelFitupRequestError, setCancelFitupRequestError] = useState<WeldActionErrorExt | null>();
  const { user } = useAppUser();
  const { envConfig } = useEnvProvider();
  const { assignedCrewMembers } = useWorkPackage();
  const { canCreateFitupRequest } = useWorkPackagePermissions(assignedCrewMembers);
  const { i18n } = useLingui();
  const activityLimit = 20;
  const [
    loadWeldDetails,
    {
      loading: weldDetailLoading,
      data: weldData,
      error: weldDetailError,
      refetch: weldDetailRefetch,
      networkStatus: weldDetailNetworkStatus,
    },
  ] = useLazyQuery<
    { commoditiesByIds: CommodityWrapper[] },
    {
      ids: Weld['id'][];
      paging: PagingInput;
    }
  >(GET_WELDS_DETAIL_BY_IDS, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'cache-first',
  });

  const onCancelFitupCompleted = (data) => {
    const error = get(data, 'cancelFitupRequest.error');
    if (error) {
      setCancelFitupRequestError(error);
    } else {
      setCancelFitupRequestError(null);
      close();
    }
  };
  const onCancelFitupError = (error) => {
    setCancelFitupRequestError(error);
  };

  const [cancelFitupRequest, { loading: cancelFitupRequestLoading }] = useCancelFitupRequest(
    onCancelFitupCompleted,
    onCancelFitupError,
    ['commodityStatsSummary']
  );

  const weldDetails: Weld = get(weldData, 'commoditiesByIds[0].weld');
  const fitupMilestoneName = WeldMilestoneType.FITUP;
  const fitupRequest = weldDetails?.fitupRequest;

  const weldCurrentMilestone = weldDetails?.milestones.find((milestone) => {
    return milestone?.type?.id === weldDetails?.currentMilestoneType?.id;
  });
  const fitupMilestone = find(weldDetails?.milestones, (item) => {
    return item.name.toUpperCase() === FitupMilestoneMock.name.toUpperCase();
  });
  const isFitupApproved = fitupMilestone && fitupMilestone.currentStatus?.codeI18n === MilestoneStatusCode.APPROVED;

  const installMilestone = find(weldDetails?.milestones, (item) => {
    return item.name.toUpperCase() !== FitupMilestoneMock.name.toUpperCase();
  });
  const isInstallNotApproved =
    installMilestone && installMilestone.currentStatus?.codeI18n !== MilestoneStatusCode.APPROVED;

  const isInstallFMAssigned =
    weldDetails?.commodityAssignees &&
    weldDetails?.commodityAssignees?.length > 0 &&
    filter(weldDetails?.commodityAssignees, (commodityAssignee) => {
      commodityAssignee?.personaType == PersonaType.FOREMAN && commodityAssignee?.assignee?.id;
    })?.length > 0;

  const canShowAssignButton = !isInstallFMAssigned && isFitupApproved && isInstallNotApproved;
  const rejectionSubReasonTypes =
    weldCurrentMilestone?.currentStatus?.rejectionSubReasonTypes &&
    weldCurrentMilestone?.currentStatus?.rejectionSubReasonTypes.length > 0
      ? weldCurrentMilestone?.currentStatus?.rejectionSubReasonTypes
      : [];
  const excludeNotApplicableAndCanceledMilestone = (milestones: CommodityMilestone[]) => {
    return filter(
      milestones,
      (milestone) =>
        ![MilestoneStatusCode.NOT_APPLICABLE, MilestoneStatusCode.CANCELED].includes(milestone.currentStatus.codeI18n)
    );
  };
  const excludeNotApplicableMilestone = (milestones: CommodityMilestone[]) => {
    return filter(
      milestones,
      (milestone) => !(MilestoneStatusCode.NOT_APPLICABLE === milestone.currentStatus.codeI18n)
    );
  };
  const excludeCanceledMilestone = (milestones: CommodityMilestone[]) => {
    return filter(milestones, (milestone) => !(MilestoneStatusCode.CANCELED === milestone.currentStatus.codeI18n));
  };
  let milestones = excludeNotApplicableAndCanceledMilestone(weldDetails?.milestones);

  const showCancelFitupRequest = () => {
    const fitupMilestoneCurrentStatus = find(weldDetails?.milestones, (item) => {
      return item.name.toUpperCase() === fitupMilestoneName;
    })?.currentStatus;

    const parsedFitupRequestCreatedAt = fitupMilestoneCurrentStatus?.createdAt
      ? parseISO(fitupMilestoneCurrentStatus?.createdAt)
      : undefined;

    return (
      canCreateFitupRequest &&
      fitupMilestoneCurrentStatus?.activity === MilestoneActivity.REQUESTED &&
      user.id == fitupMilestoneCurrentStatus?.createdBy?.id &&
      parsedFitupRequestCreatedAt &&
      isBefore(new Date(addDays(new Date(), -2).toISOString()), new Date(parsedFitupRequestCreatedAt))
    );
  };

  const hasFitupMilestone = some(milestones, (item) => {
    return item.name.toUpperCase() === fitupMilestoneName;
  });
  if (!hasFitupMilestone) {
    milestones = [FitupMilestoneMock, ...milestones];
  }
  const milestoneCardWidth = `${100 / milestones?.length}%`;

  const installCommodityAssignee = find(weldDetails?.commodityAssignees, (item) => {
    return item.personaType == PersonaType.FOREMAN;
  });

  const fitUpMilestone = find(weldDetails?.milestones, (item) => item.name.toUpperCase() === fitupMilestoneName);

  const canShowToggle = () => {
    const hasFitupMilestone = some(excludeCanceledMilestone(weldDetails?.milestones), (item) => {
      return item.name.toUpperCase() === fitupMilestoneName;
    });

    const installMilestone: CommodityMilestone | undefined = find(
      weldDetails?.milestones,
      (item) =>
        item?.currentStatus?.codeI18n == MilestoneStatusCode.PLANNED ||
        item?.currentStatus?.codeI18n == MilestoneStatusCode.UNPLANNED
    );
    if (
      (installMilestone?.id || installCommodityAssignee) &&
      (!hasFitupMilestone || fitUpMilestone?.currentStatus?.codeI18n === MilestoneStatusCode.REJECTED) &&
      !weldDetails?.fitupRequest?.isFitupRequired
    ) {
      return true;
    } else {
      return false;
    }
  };

  const onCancelFitupRequest = async () => {
    const fitupMilestoneId = find(weldDetails?.milestones, (item) => {
      return item.name.toUpperCase() === fitupMilestoneName;
    })?.id;
    if (!fitupMilestoneId) return;
    await cancelFitupRequest({
      variables: { input: { commodityMilestoneId: fitupMilestoneId } },
    });
  };

  useEffect(() => {
    if (!weldId) return;
    loadWeldDetails({
      variables: {
        ids: [weldId],
        paging: {
          cursor: '1',
          limit: activityLimit,
        },
      },
    });
  }, [weldId]);

  useEffect(() => {
    if (!weldDetails) return;
    const hasFitupMilestone =
      weldDetails?.milestones?.length > 1
        ? some(excludeNotApplicableMilestone(weldDetails?.milestones), (item) => {
            return item.name.toUpperCase() === fitupMilestoneName;
          })
        : some(
            weldDetails?.milestones,
            (item) =>
              item?.currentStatus?.codeI18n == MilestoneStatusCode.PLANNED ||
              item?.currentStatus?.codeI18n == MilestoneStatusCode.UNPLANNED
          );

    if (isFitupRequired === hasFitupMilestone) return;
    setIsFitupRequired(hasFitupMilestone);
  }, [weldDetails]);

  return (
    <>
      {(weldDetailNetworkStatus === NetworkStatus.refetch || weldDetailLoading) && (
        <Box
          p={{ sm: '500' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          css={{ height: '100%' }}
          data-testid="weld-details-loading"
        >
          <Spinner size={SpinnerSize.LARGE} />
        </Box>
      )}
      {!weldDetailLoading && weldDetailError && (
        <Box
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          flexGrow={{ sm: 1 }}
          flexDirection={{ sm: 'column' }}
          p={{ sm: '500' }}
        >
          <Box pb={{ sm: '500' }} css={{ textAlign: 'center' }}>
            <Text>
              <Trans>There was a problem loading Welds. Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Button onClick={() => weldDetailRefetch()} css={{ width: 'auto' }}>
            <Box mr={{ sm: '400' }}>
              <Icon icon="undo" />
            </Box>
            <Trans>Retry</Trans>
          </Button>
        </Box>
      )}
      {!weldDetailLoading && !weldDetailError && !weldDetails?.id && (
        <Box
          flexDirection={{ sm: 'column' }}
          alignItems={{ sm: 'center' }}
          justifyContent={{ sm: 'center' }}
          style={{ height: '100%' }}
          data-testid="no-selected-weld"
        >
          <EmptyWeldDetailIcon />
          <Box mt={{ sm: '600' }} px={{ sm: '600' }}>
            <Text size="400" family="graphik" color="grey.1000">
              <Trans>Please select a weld to view details</Trans>
            </Text>
          </Box>
        </Box>
      )}
      {!weldDetailLoading && !weldDetailError && weldDetails?.id && (
        <>
          {cancelFitupRequestError && <MilestoneError milestoneError={cancelFitupRequestError} />}
          <Box flexDirection={{ sm: 'column' }} style={{ height: '100%' }} data-testid="weld-details-container">
            <WeldTagStyled>
              <Text size="400" weight="600" color="grey.1000" family="graphik">
                {weldDetails?.tag}
              </Text>
              {canCreateFitupRequest && envConfig.appName === ProjectAppTypes.PF && (
                <>
                  {canShowToggle() && (
                    <Box>
                      <ToggleSwitch
                        disabled={
                          hasFitupMilestone && fitUpMilestone?.currentStatus?.codeI18n !== MilestoneStatusCode.REJECTED
                        }
                        isToggled={isFitupRequired}
                        onToggle={() => {
                          setIsFitupRequired(!isFitupRequired);
                        }}
                      />
                      <Box pl={{ sm: '450' }}>
                        <Text size="300" color="grey.1000">
                          <Trans>Fitup</Trans>
                        </Text>
                      </Box>
                    </Box>
                  )}
                  {!canShowToggle() && showCancelFitupRequest() && (
                    <WeldFlangeConnectionAction
                      onCancelFitupRequest={onCancelFitupRequest}
                      cancelFitupRequestLoading={cancelFitupRequestLoading}
                    />
                  )}
                </>
              )}
            </WeldTagStyled>
            <WeldMilestonesContainerStyled>
              <Box>
                {milestones?.length &&
                  milestones.map((milestone) => {
                    const isFitupMilestone = milestone?.name?.toUpperCase() == fitupMilestoneName;
                    return (
                      <ErrorBoundary key={milestone?.id}>
                        <Box
                          pr={{ sm: '400' }}
                          flexBasis={{ sm: milestoneCardWidth }}
                          data-testid={`weld-milestone-${milestone?.id}`}
                        >
                          <WeldMilestone
                            milestone={milestone}
                            disabled={
                              !isFitupRequired &&
                              milestone?.currentStatus?.codeI18n === MilestoneStatusCode.NOT_APPLICABLE
                            }
                            isFitupMilestone={isFitupMilestone && hasFitupMilestone ? false : isFitupMilestone}
                            hasFitupRequest={isFitupMilestone && hasFitupMilestone}
                            weld={weldDetails}
                            assignee={
                              (fitupRequest && fitupRequest?.installationCrewMember) ??
                              installCommodityAssignee?.assignee
                            }
                            setMilestoneData={setMilestoneData}
                            assignReassignVisible={canShowAssignButton}
                            actionLoading={cancelFitupRequestLoading}
                            showItrCertDialog={showItrCertDialog}
                          />
                        </Box>
                      </ErrorBoundary>
                    );
                  })}
              </Box>
              {weldDetails?.status?.status === CommodityStatusCode.SENT_BACK_TO_PIPING && (
                <SentBackReasonContainerStyled>
                  <Text size="300" color="grey.600">
                    <Trans>Sent Back Reason</Trans>
                  </Text>
                  <Box mt={{ sm: '300' }}>
                    <Text size="400" color="grey.1000">
                      {weldCurrentMilestone?.currentStatus?.rejectionType?.reason == RejectFitupReason.OTHER_REASON
                        ? weldCurrentMilestone?.currentStatus?.comments
                        : weldCurrentMilestone?.currentStatus?.rejectionType?.reason &&
                          i18n._(SendBackReasonMessages[weldCurrentMilestone?.currentStatus?.rejectionType?.reason])}
                    </Text>
                  </Box>
                  {rejectionSubReasonTypes.length > 0 && (
                    <Box flexDirection={{ sm: 'column' }} mt={{ sm: '500' }}>
                      <Text size="300" color="grey.600">
                        <Trans>Sent Back Sub Reasons</Trans>
                      </Text>
                      <Box mt={{ sm: '300' }}>
                        <Text size="400" color="grey.1000">
                          {join(
                            map(rejectionSubReasonTypes, (item) => {
                              return item.reason == RejectWeldSubReason.OTHER_SUB_REASON
                                ? item.comments
                                : i18n._(SendBackSubReasonMessages[item.reason]);
                            }),
                            ', '
                          )}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </SentBackReasonContainerStyled>
              )}
            </WeldMilestonesContainerStyled>
            <WeldDetailTabs
              weld={weldDetails}
              isFitupRequired={isFitupRequired}
              close={close}
              showItrCertDialog={showItrCertDialog}
            />
          </Box>
        </>
      )}
    </>
  );
};
